/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: BI Dashboard
 *| (C) Copyright IBM Corp. 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'underscore',
	'./QueryPostProcessor',
	'./LocalAggregateSortInfo',
	'./GroupedRows',
	'../../../../widgets/livewidget/query/QueryResultDataUtils'
], (_, QueryPostProcessor, LocalAggregateSortInfo, GroupedRows, QueryResultDataUtils) => {

	/**
	 * A class that will sort query result data and dataItems for grouped aggregated cases (example: stacked bar chart).
	 * The grouped data items should be sorted according to the aggregated value of the data in the group.
	 * When the data items are re-ordered, the data indices/references to data itmes will also need to be adjusted.
	 **/
	class PostProcessLocalSort extends QueryPostProcessor{

		/**
		 * @Constructor
		 * @param {Object} options Containing the visualization API and or optional localAggregateSortInfo object.
		 */
		constructor(options) {
			super(options);
			this.visualization = options.visualization;
			this.localAggregateSortInfo = options.localAggregateSortInfo || new LocalAggregateSortInfo(options.visualization); // For unit testing
		}

		/**
		 * Process Query result based on the passed options to constructor.
		 * Sort the query result data and dataItems if the visualization supports local aggregrate sort.
		 *
		 * @return {QueryResultData}
		 */
		_processData() {
			const sortInfo = this.localAggregateSortInfo.getSortInfo();
			if (this._queryResultData && this._queryResultData.data && !!sortInfo) {
				const groupedRows = this._computeGroupedRows(sortInfo);
				this._processDataPoints(groupedRows);
				this._processDataItems(groupedRows);
			}

			//Process Done
			return this._queryResultData;
		}

		/**
		 * @param {Object} data Query result data.
		 * @param {Object} softInfo Local aggregate sort details.
		 * @return {Object} GroupedRows.
		 */
		_computeGroupedRows(sortInfo) {
			// ID of category to group members by.
			const groupDataItemRef = this._getGroupDataItemRefId(sortInfo.groupDataItemIds);
			const groupIndex = QueryResultDataUtils.getDataItemIndex(this._queryResultData, groupDataItemRef);

			// ID of measure to aggregate on
			const aggregateDataItemRefId = this._getDataItemRefId(sortInfo.aggregateDataItemId);
			const aggregateIndex = QueryResultDataUtils.getDataItemIndex(this._queryResultData, aggregateDataItemRefId);

			const groupedRows = new GroupedRows(groupIndex, aggregateIndex);

			// Sort rows by their group attribute values and compute aggregate values
			// for each bin.
			let resultDataLength = this._queryResultData && this._queryResultData.data ? this._queryResultData.data.length : 0;
			for (let i = 0; i < resultDataLength; i++) {
				let row = this._queryResultData.data[i];
				groupedRows.addRow(row);
			}

			// Sort the bin keys according to their final aggregate values.
			groupedRows.sort(function (aggregateValue) {
				return sortInfo.order === 'asc' ? aggregateValue : -aggregateValue;
			});

			// Sort each bin of the groupedRows based on the data item in mappedSlotCondition slot
			const mappedSlotConditionItemIdx = QueryResultDataUtils.getDataItemIndex(this._queryResultData, sortInfo.mappedSlotConditionDataItemUid);
			for (let key in groupedRows.bins) {
				const bin = groupedRows.bins[key];
				bin.rows.sort((a, b) => {
					for (let i = 0; i < a.pt.length; i++) {
						if (i === mappedSlotConditionItemIdx) {
							return a.pt[i] - b.pt[i];
						}
					}
				});
			}

			return groupedRows;
		}

		/**
		 * Obtain the query result data item reference that corresponds to the columnId.
		 * @param {String} dataItemId data item unique ID (@look LocalAggregateSortInfo._getSlotSortInfo)
		 * @return {String}  Data item reference ID, Unique ID A.K.A Projection ID.
		 */
		_getDataItemRefId(dataItemId) {
			// ID of category to group members by.
			const mappingInfo = this.visualization.getSlots().getMappingInfo(dataItemId);
			let dataItemIdRef;
			if (mappingInfo) {
				if (mappingInfo.slot.isStacked()) {
					// If the slot is stacked, the dataItem has a ref id that matches the slot.
					dataItemIdRef = mappingInfo.slot.getId();
				} else {
					dataItemIdRef = dataItemId;
				}
			}
			return dataItemIdRef;
		}

		/**
		 * Obtain the query result data item reference that corresponds to the grouped columns.
		 * @param {Array} groupDataItemIds An array of group column unique ID's.
		 * @return {String}  Data item reference ID, Unique ID A.K.A Projection ID.
		 */
		_getGroupDataItemRefId(groupDataItemIds) {
			// If we have multiple group columns, the slot is stacked
			// We are only interested in the data item that the stacked items are both projected to.
			let groupedDataItemRefIds = [];
			_.each(groupDataItemIds, groupDataItemId => {
				const dataItemId = this._getDataItemRefId(groupDataItemId);
				if (dataItemId && groupedDataItemRefIds.indexOf(dataItemId) === -1) {
					groupedDataItemRefIds.push(dataItemId);
				}
			});

			// There should only be one group data item ref at this point
			return groupedDataItemRefIds[0];
		}

		/**
		 * Process all data points to restructure the data for grouped aggregate sort.
		 * The data will also be re-indexed to align with the sorted data items that they reference.
		 * @param {Object} groupedRows GroupedRows containing the sorted aggregrate details.
		 */
		_processDataPoints(groupedRows) {
			this._queryResultData.data = groupedRows.flatten();
		}

		/**
		 * Process the data items.
		 * The dataItem tuples will be sorted based on the aggregate values of the data in each grouping.
		 * @param {Object} groupedRows GroupedRows containing the sorted aggregrate details.
		 */
		_processDataItems(groupedRows) {
			const groupItems = this._queryResultData.dataItems[groupedRows.groupIndex].items;
			const orderedItems = _.map(groupedRows.binKeys, key => groupItems[key]);
			this._queryResultData.dataItems[groupedRows.groupIndex].items = orderedItems;
		}
	}

	return PostProcessLocalSort;
});
